import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Loader2, Building2, Globe, Landmark, Shield,
  Sparkles, CheckCircle2, Info, Crown, Layers, Home, BookOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../lib/api';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

// ---------- option definitions (kept in sync with backend pricing) ----------

const COMPLEXITY_OPTIONS = [
  {
    value: 'single',
    label: 'Single Entity',
    detail: 'One LLC or Corporation',
    icon: Building2,
    hint: '1 entity credit',
  },
  {
    value: 'multi_entity',
    label: 'Multi-Entity',
    detail: '2–3 related entities, no formal holding',
    icon: Layers,
    hint: '3 entity credits',
  },
  {
    value: 'holding_plus_subs',
    label: 'Holding + Subsidiaries',
    detail: 'Parent / child liability firewall',
    icon: Landmark,
    hint: '4 entity credits',
  },
];

const JURISDICTION_OPTIONS = [
  { value: 'single_state', label: 'Single State', detail: 'Formed and operating in one U.S. state', icon: Home },
  { value: 'multi_state', label: 'Multi-State', detail: 'Operating across multiple U.S. states', icon: Globe },
  { value: 'multi_country', label: 'Multi-Country', detail: 'International entity architecture', icon: Globe },
];

const ASSET_OPTIONS = [
  { value: 'operating', label: 'Operating business', detail: 'Revenue-generating activity', icon: Building2 },
  { value: 'real_estate', label: 'Real estate', detail: 'Rental / development / REIT-style', icon: Home },
  { value: 'ip_portfolio', label: 'IP portfolio', detail: 'Trademarks, patents, copyrights, software', icon: BookOpen },
  { value: 'trust', label: 'Asset-protection trust', detail: 'Estate-planning / wealth protection layer', icon: Shield },
];

const STATE_OPTIONS = [
  'DE', 'WY', 'NV', 'TX', 'FL', 'CA', 'NY', 'IL', 'CO', 'WA', 'MA', 'GA', 'NC',
];

// ---------- page ----------

export default function FormationWizardPage() {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const [step, setStep] = useState(0); // 0..3
  const [executive, setExecutive] = useState(false);
  const [complexity, setComplexity] = useState('single');
  const [jurisdictions, setJurisdictions] = useState('single_state');
  const [states, setStates] = useState(['DE']);
  const [assetTypes, setAssetTypes] = useState(['operating']);
  const [includeRegisteredAgent, setIncludeRegisteredAgent] = useState(true);

  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  const inputs = useMemo(() => ({
    complexity,
    jurisdictions,
    states,
    asset_types: assetTypes,
    executive,
    include_registered_agent: includeRegisteredAgent,
  }), [complexity, jurisdictions, states, assetTypes, executive, includeRegisteredAgent]);

  const fetchQuote = useCallback(async () => {
    setLoadingQuote(true);
    try {
      const { data } = await api.post('/formation/quote', inputs);
      setQuote(data);
    } catch (e) {
      console.error('Quote error:', e);
      toast.error(e.response?.data?.detail || 'Could not compute quote');
    } finally {
      setLoadingQuote(false);
    }
  }, [inputs]);

  useEffect(() => {
    // Auto-refresh quote as inputs change (debounced via microtask)
    const t = setTimeout(fetchQuote, 200);
    return () => clearTimeout(t);
  }, [fetchQuote]);

  const toggleAsset = (v) => {
    setAssetTypes((prev) => {
      const has = prev.includes(v);
      if (has) {
        const next = prev.filter((a) => a !== v);
        return next.length === 0 ? ['operating'] : next;
      }
      return [...prev, v];
    });
  };

  const toggleState = (v) => {
    setStates((prev) => {
      const has = prev.includes(v);
      if (has) {
        const next = prev.filter((s) => s !== v);
        return next.length === 0 ? ['DE'] : next;
      }
      return [...prev, v];
    });
  };

  const handleAddToCart = () => {
    if (!quote) return;
    addItem('formation_package', 1, {
      priceUsd: quote.total_usd,
      priceUsdc: quote.total_usdc,
      formationInputs: inputs,
      entityCredits: quote.entity_credits_awarded,
      aLaCarteSavings: quote.a_la_carte_savings_usd || 0,
      label: executive ? 'Executive Formation Package' : 'Formation Package',
    });
    toast.success(`${quote.entity_credits_awarded} entity credits will be added on checkout`);
    navigate('/cart');
  };

  const canNext = () => {
    if (step === 0) return true;
    if (step === 1) return !!complexity && !executive || executive;
    if (step === 2) return states.length > 0 && assetTypes.length > 0;
    return true;
  };

  // ---------- render ----------
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="formation-wizard-page">
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
          data-testid="formation-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit']">Build Your Structure</h1>
          <p className="text-slate-400 mt-2">
            Four questions. We calculate your platform fee + state filing fees, then award you
            prepaid entity credits you can claim over time.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8" data-testid="formation-progress">
          {['Package', 'Structure', 'Details', 'Review'].map((label, i) => (
            <div key={label} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 ${i <= step ? 'text-blue-400' : 'text-slate-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                  i < step ? 'bg-blue-500 text-white' :
                  i === step ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-300' :
                  'bg-slate-900 border border-slate-800'
                }`}>
                  {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                </div>
                <span className="text-sm hidden sm:inline">{label}</span>
              </div>
              {i < 3 && <div className={`h-px flex-1 mx-2 ${i < step ? 'bg-blue-500' : 'bg-slate-800'}`} />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ---------- main wizard ---------- */}
          <div className="lg:col-span-2 space-y-6">
            <div className="stat-card min-h-[400px]">
              {/* Step 0: Executive / Custom */}
              {step === 0 && (
                <div data-testid="formation-step-package">
                  <h2 className="text-xl font-semibold mb-1">Pick your starting point</h2>
                  <p className="text-sm text-slate-400 mb-6">
                    Executive locks a flat fee for a full multi-entity, multi-state holding + trust + IP structure.
                    Or build a custom package below.
                  </p>

                  <button
                    onClick={() => setExecutive(true)}
                    data-testid="formation-executive-card"
                    className={`w-full text-left p-5 rounded-xl border-2 transition mb-3 ${
                      executive ? 'border-amber-500 bg-amber-500/10' : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Crown className="w-5 h-5 text-amber-400" />
                      <span className="font-semibold text-lg">Executive Package</span>
                      <span className="ml-auto font-mono text-amber-300">$12,500 flat</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      7 entity credits · Holding + Subs + Trust + IP · Multi-state qualification · Registered agents included
                    </p>
                  </button>

                  <button
                    onClick={() => setExecutive(false)}
                    data-testid="formation-custom-card"
                    className={`w-full text-left p-5 rounded-xl border-2 transition ${
                      !executive ? 'border-blue-500 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Sparkles className="w-5 h-5 text-blue-400" />
                      <span className="font-semibold text-lg">Custom Package</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Pick the exact complexity + states + asset types you need. Price adjusts live.
                    </p>
                  </button>
                </div>
              )}

              {/* Step 1: Complexity (custom only) */}
              {step === 1 && (
                <div data-testid="formation-step-structure">
                  <h2 className="text-xl font-semibold mb-1">How many entities do you need?</h2>
                  <p className="text-sm text-slate-400 mb-6">
                    This determines the number of prepaid entity formation credits awarded.
                  </p>

                  {executive ? (
                    <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <p className="text-sm text-amber-200">
                        <Crown className="w-4 h-4 inline mr-1" />
                        Executive Package selected — locked at 7 entity credits.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {COMPLEXITY_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = complexity === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setComplexity(opt.value)}
                            data-testid={`complexity-${opt.value}`}
                            className={`text-left p-4 rounded-xl border-2 transition flex items-center gap-4 ${
                              selected ? 'border-blue-500 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700'
                            }`}
                          >
                            <Icon className={`w-6 h-6 ${selected ? 'text-blue-400' : 'text-slate-500'}`} />
                            <div className="flex-1">
                              <div className="font-semibold">{opt.label}</div>
                              <div className="text-xs text-slate-400">{opt.detail}</div>
                            </div>
                            <span className={`text-xs font-mono px-2 py-1 rounded ${selected ? 'bg-blue-500/20 text-blue-300' : 'bg-slate-800 text-slate-500'}`}>
                              {opt.hint}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Jurisdictions + assets */}
              {step === 2 && (
                <div className="space-y-8" data-testid="formation-step-details">
                  {/* Scope */}
                  <div>
                    <h2 className="text-xl font-semibold mb-1">Where will these entities operate?</h2>
                    <p className="text-sm text-slate-400 mb-4">Single state is cheapest; multi-country adds complexity.</p>
                    <div className="grid gap-3">
                      {JURISDICTION_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = jurisdictions === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setJurisdictions(opt.value)}
                            data-testid={`jurisdiction-${opt.value}`}
                            className={`text-left p-3 rounded-xl border-2 transition flex items-center gap-3 ${
                              selected ? 'border-blue-500 bg-blue-500/10' : 'border-slate-800 hover:border-slate-700'
                            }`}
                          >
                            <Icon className={`w-5 h-5 ${selected ? 'text-blue-400' : 'text-slate-500'}`} />
                            <div className="flex-1">
                              <div className="font-medium">{opt.label}</div>
                              <div className="text-xs text-slate-400">{opt.detail}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* States */}
                  <div>
                    <Label className="text-slate-300 block mb-2">
                      Pick states for initial filings (government fees pass-through)
                    </Label>
                    <div className="flex flex-wrap gap-2" data-testid="state-picker">
                      {STATE_OPTIONS.map((s) => {
                        const selected = states.includes(s);
                        return (
                          <button
                            key={s}
                            onClick={() => toggleState(s)}
                            data-testid={`state-${s}`}
                            className={`px-3 py-1.5 rounded-lg text-sm font-mono border transition ${
                              selected ? 'border-blue-500 bg-blue-500/10 text-blue-300' : 'border-slate-800 text-slate-400 hover:border-slate-700'
                            }`}
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Assets */}
                  <div>
                    <h3 className="font-semibold mb-1">What assets will live inside?</h3>
                    <p className="text-sm text-slate-400 mb-4">Multi-select. Certain asset types trigger extra structure layers.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {ASSET_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = assetTypes.includes(opt.value);
                        return (
                          <button
                            key={opt.value}
                            onClick={() => toggleAsset(opt.value)}
                            data-testid={`asset-${opt.value}`}
                            className={`text-left p-3 rounded-xl border-2 transition flex items-start gap-3 ${
                              selected ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-800 hover:border-slate-700'
                            }`}
                          >
                            <Icon className={`w-5 h-5 mt-0.5 ${selected ? 'text-emerald-400' : 'text-slate-500'}`} />
                            <div>
                              <div className="font-medium text-sm">{opt.label}</div>
                              <div className="text-xs text-slate-400">{opt.detail}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* RA toggle */}
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeRegisteredAgent}
                      onChange={(e) => setIncludeRegisteredAgent(e.target.checked)}
                      data-testid="include-registered-agent"
                      className="mt-1 accent-blue-500"
                    />
                    <span className="text-sm text-slate-300">
                      Include registered agent service (1 year, per state) —{' '}
                      <span className="text-slate-500">recommended</span>
                    </span>
                  </label>
                </div>
              )}

              {/* Step 3: Review */}
              {step === 3 && quote && (
                <div data-testid="formation-step-review">
                  <h2 className="text-xl font-semibold mb-1">Review your quote</h2>
                  <p className="text-sm text-slate-400 mb-6">
                    Platform fees one-time · Government fees pass-through at face value.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-slate-500 mb-2">Platform Services (CorpCraft)</h3>
                      <div className="space-y-2">
                        {quote.platform_line_items.map((li, i) => (
                          <div key={i} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg p-3">
                            <div>
                              <div className="text-sm font-medium">{li.label}</div>
                              <div className="text-xs text-slate-400">{li.detail}</div>
                            </div>
                            <div className="font-mono text-sm">${li.amount_usd.toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-sm uppercase tracking-wider text-slate-500 mb-2">Government Filing Fees (pass-through)</h3>
                      <div className="space-y-2">
                        {quote.government_line_items.length === 0 && (
                          <div className="text-xs text-slate-500 italic p-3">No government fees for this configuration.</div>
                        )}
                        {quote.government_line_items.map((li, i) => (
                          <div key={i} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg p-3">
                            <div>
                              <div className="text-sm font-medium">{li.label}</div>
                              <div className="text-xs text-slate-400">{li.detail}</div>
                            </div>
                            <div className="font-mono text-sm">${li.amount_usd.toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {quote.notes?.length > 0 && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200 space-y-1">
                        {quote.notes.map((n, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <span>{n}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Nav */}
              <div className="flex items-center justify-between pt-8 mt-8 border-t border-slate-800">
                <Button
                  variant="ghost"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="formation-prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                {step < 3 ? (
                  <Button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canNext()}
                    className="gradient-accent"
                    data-testid="formation-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    disabled={!quote || loadingQuote}
                    className="gradient-accent"
                    data-testid="formation-add-to-cart-btn"
                  >
                    {loadingQuote ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Pricing…</>
                    ) : (
                      <>Add to cart <ArrowRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* ---------- sticky quote sidebar ---------- */}
          <div className="lg:col-span-1">
            <div className="stat-card sticky top-6" data-testid="formation-quote-sidebar">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <h3 className="font-semibold">Live Quote</h3>
                {loadingQuote && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500 ml-auto" />}
              </div>

              {!quote ? (
                <div className="text-sm text-slate-500 py-6 text-center">Pick options to see your price…</div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-400">Platform fee</span>
                    <span className="font-mono text-sm">${quote.platform_fee_usd.toLocaleString()}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-400">Government fees</span>
                    <span className="font-mono text-sm">${quote.government_fees_usd.toLocaleString()}</span>
                  </div>

                  <div className="border-t border-slate-800 pt-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-slate-300 font-medium">Total (USD)</span>
                      <span className="font-mono text-2xl font-bold" data-testid="formation-total-usd">
                        ${quote.total_usd.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-1">
                      <span className="text-xs text-emerald-400">USDC (-8% on platform)</span>
                      <span className="font-mono text-sm text-emerald-400" data-testid="formation-total-usdc">
                        ${quote.total_usdc.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                    <div className="text-xs text-blue-300 uppercase tracking-wider">Prepaid credits awarded</div>
                    <div className="text-3xl font-bold text-blue-200" data-testid="formation-credits-awarded">
                      {quote.entity_credits_awarded}
                    </div>
                    <div className="text-xs text-blue-300">entity formations</div>
                  </div>

                  {quote.a_la_carte_savings_usd > 0 && (
                    <div
                      data-testid="formation-savings-callout"
                      className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2"
                    >
                      <Sparkles className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                      <div className="text-xs">
                        <div className="text-emerald-200 font-semibold">
                          Save ${quote.a_la_carte_savings_usd.toLocaleString()} vs. a la carte
                        </div>
                        <div className="text-emerald-300/70">
                          cheaper than {quote.entity_credits_awarded} single-entity packages
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
